<template>
  <div class="date-picker">
    <date-picker
      :value="formatInputValue"
      :format="formatString"
      value-type="timestamp"
      type="datetime"
      :lang="currentLocale"
      :clearable="clearable"
      :disabled-date="disabledAfterToday"
      :minute-step="1"
      :hour-format="use24Hours ? 24 : 12"
      :hour-options="hourOptions"
      :confirm="true"
      :confirm-text="confirmText"
      @change="onDateTimeChange"
      @confirm="onDateTimeConfirm"
      :placeholder="placeholder ? $t(placeholder) : null"
    >
    </date-picker>
  </div>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';

export default {
  name: 'DateTimePicker',
  components: {
    DatePicker,
  },
  props: {
    value: {
      type: String,
      default: null
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    disableAfterToday: {
      type: Boolean,
      default: true,
    },
    disableBeforeToday: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: '',
    },
    use24Hours: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      tempValue: null,
      locales: {
        km: {
          formatLocale: {
            months: [
              "មករា", "កុម្ភៈ", "មីនា", "មេសា", "ឧសភា", "មិថុនា",
              "កក្កដា", "សីហា", "កញ្ញា", "តុលា", "វិច្ឆិកា", "ធ្នូ"
            ],
            monthsShort: [
              "មករា", "កុម្ភៈ", "មីនា", "មេសា", "ឧសភា", "មិថុនា",
              "កក្កដា", "សីហា", "កញ្ញា", "តុលា", "វិច្ឆិកា", "ធ្នូ"
            ],
            weekdays: [
              "អាទិត្យ", "ច័ន្ទ", "អង្គារ", "ពុធ",
              "ព្រហស្បតិ៍", "សុក្រ", "សៅរ៍"
            ],
            weekdaysShort: ["អា", "ច", "អ", "ពុ", "ព្រ", "សុ", "សៅ"],
            weekdaysMin: ["អា", "ច", "អ", "ពុ", "ព្រ", "សុ", "សៅ"],
            firstDayOfWeek: 0,
            firstWeekContainsDate: 1,
            meridiem: (h, _, isLowercase) => {
              const word = h < 12 ? 'ព្រឹក' : 'ល្ងាច';
              return isLowercase ? word.toLowerCase() : word;
            }
          },
          buttonText: {
            ok: 'យល់ព្រម',
            cancel: 'បោះបង់'
          },
        },
        'zh-cn': {
          formatLocale: {
            months: [
              "一月", "二月", "三月", "四月", "五月", "六月",
              "七月", "八月", "九月", "十月", "十一月", "十二月"
            ],
            monthsShort: [
              "一月", "二月", "三月", "四月", "五月", "六月",
              "七月", "八月", "九月", "十月", "十一月", "十二月"
            ],
            weekdays: [
              "星期日", "星期一", "星期二", "星期三",
              "星期四", "星期五", "星期六"
            ],
            weekdaysShort: ["周日", "周一", "周二", "周三", "周四", "周五", "周六"],
            weekdaysMin: ["周日", "周一", "周二", "周三", "周四", "周五", "周六"],
            firstDayOfWeek: 0,
            firstWeekContainsDate: 1,
            meridiem: (h, _, isLowercase) => {
              const word = h < 12 ? '上午' : '下午';
              return isLowercase ? word.toLowerCase() : word;
            }
          },
          yearFormat: "YYYY年",
          monthFormat: "M月",
          monthBeforeYear: false,
          buttonText: {
            ok: '确定',
            cancel: '取消'
          }
        },
        vi: {
          formatLocale: {
            months: [
              "Tháng 1", "Tháng 2", "Tháng 3", "Tháng 4", "Tháng 5", "Tháng 6",
              "Tháng 7", "Tháng 8", "Tháng 9", "Tháng 10", "Tháng 11", "Tháng 12"
            ],
            monthsShort: [
              "Th1", "Th2", "Th3", "Th4", "Th5", "Th6",
              "Th7", "Th8", "Th9", "Th10", "Th11", "Th12"
            ],
            weekdays: [
              "Chủ nhật", "Thứ hai", "Thứ ba", "Thứ tư",
              "Thứ năm", "Thứ sáu", "Thứ bảy"
            ],
            weekdaysShort: ["CN", "T2", "T3", "T4", "T5", "T6", "T7"],
            weekdaysMin: ["CN", "T2", "T3", "T4", "T5", "T6", "T7"],
            firstDayOfWeek: 0,
            firstWeekContainsDate: 1,
            meridiem: (h, _, isLowercase) => {
              const word = h < 12 ? 'SA' : 'CH';
              return isLowercase ? word.toLowerCase() : word;
            }
          },
          buttonText: {
            ok: 'Đồng ý',
            cancel: 'Hủy'
          }
        }
      }
    };
  },
  computed: {
    formatInputValue() {
      if (!this.value) return null;
      return new Date(this.value).getTime();
    },
    currentLocale() {
      const locale = this.$i18n.locale;
      return this.locales[locale] || {};
    },
    confirmText() {
      const locale = this.$i18n.locale;
      const localeConfig = this.locales[locale] || {};
      return localeConfig?.buttonText?.ok || 'Ok';
    },
    hourOptions() {
      return this.use24Hours
        ? Array.from({ length: 24 }, (_, i) => i)
        : Array.from({ length: 12 }, (_, i) => i);
    },
    formatString() {
      return this.use24Hours ? 'DD MMMM YYYY HH:mm' : 'DD MMMM YYYY hh:mm A';
    },
  },
  methods: {
    disabledAfterToday(date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (this.disableBeforeToday && date < today) {
        return true;
      }

      if (this.disableAfterToday && date > today) {
        return true;
      }

      return false;
    },
    onDateTimeChange(timestamp) {
      // This will fire when date/time changes but before confirming
      if (!timestamp) {
        this.tempValue = null;
        return;
      }
      this.tempValue = timestamp;
    },
    onDateTimeConfirm(timestamp) {
      if (!timestamp) {
        this.$emit('input', null);
        return;
      }

      const date = new Date(timestamp);
      this.$emit('input', date.toISOString());
    }
  }
};
</script>

<style scoped>
.date-picker {
  width: 100%;
}

.mx-datepicker {
  width: 100%;
}
</style>